import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

import {
    QuickTurnBoltMaterial,
    QuickTurnBoltThread,
    QuickTurnBoltHeadType,
    QuickTurnBoltHeadStandard,
} from '@partsbadger/types';
import { get, patch, post, remove, API_ROOT, getAuthToken } from '@partsbadger/utils';
import QuickTurnPartStore from './QuickTurnPartStore';

const checkIsValidUUID = (id: string) => {
    const regex = new RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
    return regex.test(id);
};

function getHeader(has_files = false) {
    const token = getAuthToken();

    if (token) {
        return {
            'Content-Type': has_files ? 'multipart/form-data' : 'application/json',
            Authorization: `Token ${token}`,
        };
    } else {
        return {
            'Content-Type': has_files ? 'multipart/form-data' : 'application/json',
        };
    }
}

interface IUpdateBoltProps {
    name?: string;
    quantity?: number;
    material?: string;
    use_standard?: boolean;
    standard?: string;
    head?: string;
    use_custom_thread?: boolean;
    threading?: string;
    thread?: string;
    custom_thread_major_diameter_inch?: number;
    custom_thread_pitch_inch?: number;
    thread_length?: number;
    shaft_length?: number;
    bolt_head_type?: string;
}

class QuickTurnBoltStore {
    loading = false;

    materials?: QuickTurnBoltMaterial[];
    standards: QuickTurnBoltHeadStandard[] = [];
    threadSizes: QuickTurnBoltThread[] = [];
    boltHeadTypes: QuickTurnBoltHeadType[] = [];

    name?: string;
    material: string | null = null;
    use_standard?: boolean;
    standard: string | null = null;
    use_custom_thread?: boolean;
    thread: string | null = null;
    threading = 'Partially Threaded';
    thread_length = 0;
    shaft_length = 0;
    bolt_head_type: string | null = null;
    bolt_head_diameter = 0;

    custom_thread_major_diameter_inch?: number;
    custom_thread_major_diameter_mm?: number;
    custom_thread_pitch_inch?: number;
    custom_thread_pitch_mm?: number;

    quantity = 1;
    qtyOnePrice = 0;
    price = 0;
    bufferGeometry: any;

    boltToken: string | null = localStorage.getItem('bolt_token') ? localStorage.getItem('bolt_token') : null;

    constructor() {
        makeAutoObservable(this);
    }

    init() {
        this.getMaterials();
        this.getStandards();
        this.getBoltHeadTypes();
        this.getThreadSizes();
    }

    setBoltToken(token: string | null) {
        this.boltToken = token;
    }

    setMaterials(value: QuickTurnBoltMaterial[]) {
        this.materials = value;
    }

    setStandards(value: QuickTurnBoltHeadStandard[]) {
        this.standards = value;
    }

    setThreadSizes(value: QuickTurnBoltThread[]) {
        this.threadSizes = value;
    }

    setBoltHeadTypes(value: QuickTurnBoltHeadType[]) {
        this.boltHeadTypes = value;
    }

    setBoltHeadType(value: string) {
        this.bolt_head_type = value;
    }

    setBoltHeadDiameter(value: number) {
        this.bolt_head_diameter = value;
    }

    setName(value: string) {
        this.name = value;
    }

    setThreading(value: string) {
        this.threading = value;
    }

    setShaftLength(value: number) {
        this.shaft_length = Number(value?.toFixed(3));
    }

    setThreadLength(value: number) {
        this.thread_length = Number(value?.toFixed(3));
    }

    setMaterial(value: string) {
        this.material = value;
    }

    setUseStandard(value: boolean) {
        this.use_standard = value;
    }

    setStandard(value: string) {
        this.standard = value;
    }

    setUseCustomThread(value: boolean) {
        this.use_custom_thread = value;
    }

    setCustomThreadMajorDiameterInch(value: number) {
        this.custom_thread_major_diameter_inch = Number(value?.toFixed(3));
    }

    setCustomThreadPitchInch(value: number) {
        this.custom_thread_pitch_inch = Number(value?.toFixed(3));
    }

    setThread(value: string) {
        this.thread = value;
    }

    setQuantity(value: number) {
        this.quantity = value;
    }

    setQtyOnePrice(value: number) {
        this.qtyOnePrice = value;
    }

    setPrice(value: number) {
        this.price = value;
    }

    setBufferGeometry(value: any) {
        this.bufferGeometry = value;
    }

    async getMaterials() {
        try {
            const data = await get(`/quickturn/api/bolts/materials`);

            this.setMaterials(data);
        } catch (err) {
            message.error(`Error: ${err}`);

            return err;
        }
    }

    async getStandards() {
        try {
            const data = await get(`/quickturn/api/bolts/standards`);

            this.setStandards(data);
        } catch (err) {
            message.error(`Error: ${err}`);

            return err;
        }
    }

    async getThreadSizes() {
        try {
            const data = await get(`/quickturn/api/bolts/thread-sizes`);

            this.setThreadSizes(data);
        } catch (err) {
            message.error(`Error: ${err}`);

            return err;
        }
    }

    async getBoltHeadTypes() {
        try {
            const data = await get(`/quickturn/api/bolts/head-types`);

            this.setBoltHeadTypes(data);
        } catch (err) {
            message.error(`Error: ${err}`);

            return err;
        }
    }

    async startNewBolt(values?: any) {
        try {
            this.loading = true;

            const data = await post(`/quickturn/api/bolts/`, values);

            if (data?.id) {
                localStorage.setItem('bolt_token', data['id']);

                this.boltToken = data['id'];

                this.getBoltByID(data['id']);
            }

            this.loading = false;
        } catch (err) {
            this.loading = false;

            message.error(`Error: ${err}`);
            return err;
        }
    }

    async getBoltByID(id: string) {
        try {
            this.loading = true;

            const isValidUUID = checkIsValidUUID(id);

            if (isValidUUID) {
                const data = await get(`/quickturn/api/bolts/${id}/`);

                this.setName(data?.name);
                this.setQuantity(data?.quantity);
                this.setMaterial(data?.material?.id);
                this.setUseStandard(data?.use_standard);
                this.setStandard(data?.standard?.id);
                this.setUseCustomThread(data?.use_custom_thread);
                this.setCustomThreadMajorDiameterInch(data?.custom_thread_major_diameter_inch);
                this.setCustomThreadPitchInch(data?.custom_thread_pitch_inch);
                this.setThread(data?.thread?.id);
                this.setShaftLength(data?.shaft_length);
                this.setThreadLength(data?.thread_length);
                this.setBoltHeadType(data?.bolt_head_type?.id);
                this.setBoltHeadDiameter(data?.bolt_head_diameter);
                this.setQtyOnePrice(data?.price);
                this.setPrice(data?.volume_price_single);

                this.setBufferGeometry(JSON.stringify(data?.get_buffer_geometry));
            } else {
                localStorage.removeItem('bolt_token');
                this.startNewBolt();
            }

            this.loading = false;
        } catch (err) {
            localStorage.removeItem('bolt_token');
            this.startNewBolt();

            this.loading = false;

            return;
        }
    }

    /**
     * This function sends updated part specifications to the API endpoint,
     * and returns the generated buffer geometry but does not save the changes to the database.
     **/
    async previewPartById(id: string) {
        try {
            this.loading = true;

            const payload = {
                name: this.name,
                quantity: this.quantity,
                material: this.material,
                thread_length: this.thread_length,
                shaft_length: this.shaft_length,
                thread: this.thread,
                bolt_head_type: this.bolt_head_type,
                bolt_head_diameter: this.bolt_head_diameter,
            };

            const data = await post(`/quickturn/api/bolts/${id}/preview/`, payload);

            this.setQtyOnePrice(data?.price);
            this.setPrice(data?.volume_price_single);

            this.setBufferGeometry(JSON.stringify(data?.get_buffer_geometry));

            this.loading = false;
        } catch (err) {
            this.loading = false;
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async updateBoltById(id: string | null, values: IUpdateBoltProps) {
        try {
            this.loading = true;
            const data = await patch(`/quickturn/api/bolts/${id}/`, values);

            this.setName(data?.name);
            this.setQuantity(data?.quantity);
            this.setMaterial(data?.material?.id);
            this.setUseStandard(data?.use_standard);
            this.setStandard(data?.standard?.id);
            this.setUseCustomThread(data?.use_custom_thread);
            this.setCustomThreadMajorDiameterInch(data?.custom_thread_major_diameter_inch);
            this.setCustomThreadPitchInch(data?.custom_thread_pitch_inch);
            this.setThread(data?.thread?.id);
            this.setShaftLength(data?.shaft_length);
            this.setThreadLength(data?.thread_length);
            // this.setBoltHeadType(data?.head?.type?.id);
            this.setBoltHeadType(data?.bolt_head_type?.id);
            this.setBoltHeadDiameter(data?.bolt_head_diameter);
            this.setQtyOnePrice(data?.price);
            this.setPrice(data?.volume_price_single);

            this.setBufferGeometry(JSON.stringify(data?.get_buffer_geometry));

            this.loading = false;
        } catch (err) {
            this.loading = false;
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async updatePartQuantity(id: string, quantity: number) {
        try {
            this.loading = true;

            const payload = {
                quantity: quantity,
            };

            const data = await patch(`/quickturn/api/bolts/${id}/`, payload);

            this.setQtyOnePrice(data?.price);
            this.setPrice(data?.price);

            this.loading = false;
        } catch (err) {
            this.loading = false;
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async updatePartName(name: string) {
        try {
            this.loading = true;

            const payload = {
                name: name,
            };
            const data = await patch(`/quickturn/api/bolts/${this.boltToken}/`, payload);

            this.setName(data?.name);

            this.loading = false;
        } catch (err) {
            this.loading = false;
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async addItemToSalesOrder() {
        try {
            if (!QuickTurnPartStore.orderToken) {
                await QuickTurnPartStore.createSalesOrder();
            }

            const payload = {
                name: this.name,
                order: QuickTurnPartStore.orderToken,
                part: this.boltToken,
                quantity: this.quantity,
                unit_price: this.price,
            };

            const data = await post(`/quickturn/api/sales-order/${QuickTurnPartStore.orderToken}/add-item/`, payload);

            QuickTurnPartStore.orderData = data;

            return data;
        } catch (err) {
            message.error(`Error: ${err}`);
            return err;
        }
    }

    async addPartToCart() {
        await this.addItemToSalesOrder();

        localStorage.removeItem('bolt_token');

        await this.startNewBolt();
    }

    async removeItemFromOrder(id: string) {
        try {
            const data = await remove(`/quickturn/api/sales-order/${QuickTurnPartStore.orderToken}/remove-item/${id}/`);

            QuickTurnPartStore.orderData = data;
        } catch (err) {
            return err;
        }
    }

    async updateSalesOrderItem(payload: { line_item: string; quantity?: number }) {
        try {
            const data = await patch(
                `/quickturn/api/sales-order/${QuickTurnPartStore.orderToken}/update-item/`,
                payload
            );

            QuickTurnPartStore.orderData = data;
        } catch (err) {
            message.error(`Error: ${err}`);
            return err;
        }
    }
}

export default new QuickTurnBoltStore();
